// ============================================================================
// Project Work — Draft → Ready_to_Invoice advance gate (single source of truth).
//
// THE ONLY place the gate's condition AND its words are written. project-detail
// imports both `canAdvance` and `getAdvanceBlockers` from here; the board card
// imports `getAdvanceBlockers`. Neither writes the sentence itself any more.
//
// The gate requires a completion date, a bill amount STATED, and a pay amount
// STATED. AFB-0082, 2026-08-19 — these three were `> 0` (PR #33, 2026-06-03). A
// stated $0 is a decision and a NULL is an omission, so the test is for stated.
// Five of the six event classes normally bill the client nothing, and `> 0` made
// every one of those records unadvanceable.
//
// 2026-09-02 — NAME THE LINE. `deriveAmounts` returns a NULL project total the
// moment ANY line is missing a rate, so both surfaces said "no bill amount" on a
// four-line job whose fault was line 3. The per-line facts were computed and
// then discarded one function upstream. This helper reads the lines it is given
// and says which one, because "no bill amount" on a job that plainly has bill
// amounts on it is three true words carrying no information.
//
// ⛔ The two surfaces had ALREADY drifted under a comment promising they had
// not: the card said "bill amount" and the detail page said "no bill amount", in
// a different order. A comment cannot hold two copies of a sentence together.
// `advance-gate.lockstep.test.ts` fails if project-detail.tsx grows its own copy
// again.
//
// ⛔ TCSD-0062 — a NULL `service_code` is meaningful (a chargeback is not work,
// and the OTHER escape hatch carries its name in `scope_text`). The line label
// falls back to the AM's own scope words and never invents a service name.
// ============================================================================
import type { ProjectServiceLine } from '../../../shared/types';
import { projectWorkLines } from './project-service-label';
import { PROJECT_EVENT_CLASS } from '../hooks/use-service-catalog';

/**
 * Minimal structural shape this gate needs. Satisfied by both ProjectRow and the
 * board-card row — both already carry `project_services`, which is how the card
 * renders its service label — so one signature serves both surfaces.
 *
 * `project_services` is OPTIONAL. Absent or empty, the gate degrades to the
 * project-level wording it has always used. It never guesses at a line.
 */
export interface AdvanceGateFields {
  completion_date: string | null;
  bill_amount: number | null;
  pay_amount: number | null;
  project_services?: ProjectServiceLine[] | null;
  /**
   * WHAT KIND OF RECORD THIS IS. `'Project'` (or absent, which defaults to it)
   * is project work and MUST carry money. Every other class — Recovery, Coverage,
   * Concession, Correction, Startup — is a note to Finance, and money is optional.
   * Ben, 2026-09-03. See `moneyRequired()`.
   */
  event_class?: string | null;
}

/**
 * One line, named the way the AM would name it.
 *
 * catalog display name → the AM's own scope words → the raw code → `line N`.
 * The scope words come before the code because a line with no `service_code` is
 * the OTHER escape hatch or a chargeback, and in both cases the words the AM
 * typed ARE the name of the thing (TCSD-0062).
 */
export function advanceGateLineLabel(line: ProjectServiceLine, position: number): string {
  const ordinal = `line ${position}`;
  const scope = line.scope_text?.trim();
  const display = line.service_catalog?.display_name?.trim();

  // The escape hatch has no meaningful name of its own — "Other - describe it"
  // tells the AM nothing about which of their lines is short.
  if (display && line.service_code !== 'OTHER') return `${ordinal} · ${display}`;
  if (scope) return `${ordinal} · ${scope}`;
  if (line.service_code) return `${ordinal} · ${line.service_code}`;
  return ordinal;
}

/** "a", "a and b", "a, b and c". */
function joinLabels(labels: string[]): string {
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * The work lines missing a rate, labelled, in line order.
 *
 * Chargebacks are excluded — a chargeback never bills the client and carries its
 * own completeness check (`chargebackBlocker`), so it can never be the reason a
 * bill total is NULL. This mirrors `deriveAmounts`, which filters them the same
 * way; if the two ever disagreed the gate would name an innocent line.
 */
function unratedLabels(
  project: AdvanceGateFields,
  which: 'client_rate' | 'sub_rate',
): string[] {
  return projectWorkLines(project)
    .map((line, i) => ({ line, position: line.line_order ?? i + 1 }))
    .filter(({ line }) => line[which] == null)
    .map(({ line, position }) => advanceGateLineLabel(line, position));
}

/**
 * Missing things blocking a Draft → Ready_to_Invoice advance, in display order
 * (bill, pay, completion date). Empty array = ready to advance.
 *
 * ⛔ This is the ONLY copy of these words. Do not restate them at a call site.
 */
/**
 * ⛔ MONEY IS REQUIRED ON PROJECT WORK AND OPTIONAL ON EVERYTHING ELSE.
 * Ben, 2026-09-03: *"You keep asking me about the 'does it need money' and the
 * answer for project work is 'yes'. HOWEVER, for billing comms, the answer 'no'
 * is acceptable."*
 *
 * This is the fix behind AFB-0085. An AM covering a shift has no figure to give —
 * the hours are already on a timecard and Jose applies the rate — and the screen
 * used to refuse the record anyway, so the AM either invented a number or
 * abandoned it. A note reaching Finance with a building, a date, a sub and an
 * explanation IS complete.
 *
 * ⛔ It reads `event_class`, which already exists and already drives the
 * "say what happened, in words" requirement (`isOffProject` in project-detail).
 * No new column, no new flag, no checkbox.
 *
 * A missing class means project work, deliberately: 458 of 477 rows are `Project`
 * and the safe default on an unknown record is to ASK for the money, not to
 * excuse it.
 */
export function moneyRequired(project: AdvanceGateFields): boolean {
  return (project.event_class ?? PROJECT_EVENT_CLASS) === PROJECT_EVENT_CLASS;
}

export function getAdvanceBlockers(project: AdvanceGateFields): string[] {
  const blockers: string[] = [];
  const needsMoney = moneyRequired(project);

  if (needsMoney && project.bill_amount == null) {
    const short = unratedLabels(project, 'client_rate');
    blockers.push(short.length > 0 ? `no client rate on ${joinLabels(short)}` : 'no bill amount');
  }

  if (needsMoney && project.pay_amount == null) {
    const short = unratedLabels(project, 'sub_rate');
    blockers.push(short.length > 0 ? `no sub payment on ${joinLabels(short)}` : 'no sub payment amount');
  }

  // ⛔ The date is still required on EVERY class. Finance cannot place a note in a
  // period without one, and "roughly when" is a question every AM can answer.
  if (!project.completion_date) blockers.push('no completion date');

  return blockers;
}

/**
 * The stages that mean THE WORK HAS NOT REACHED FINANCE YET.
 *
 * ⛔ Both of them render as "Not sent" (addendum 2D) — they are the same thing to
 * an AM, and the only difference is whether Finance has looked at it once and
 * handed it back. Anything that asks "is this still with the AM?" asks HERE, so
 * a bounced-back card cannot quietly miss a badge that a fresh draft gets.
 */
export const NOT_SENT_STAGES = ['Draft', 'Awaiting_AM_Input'] as const;

export function isNotSent(stage: string | null | undefined): boolean {
  return stage != null && (NOT_SENT_STAGES as readonly string[]).includes(stage);
}

/**
 * The gate itself. Identical by construction to `getAdvanceBlockers(...).length
 * === 0` — the two cannot disagree, because this IS that expression. The button
 * and the sentence beside it are now one fact rather than two that must be kept
 * in step by hand.
 */
export function canAdvance(project: AdvanceGateFields): boolean {
  return getAdvanceBlockers(project).length === 0;
}
